import { fromPromise } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import {
  AuthErrorCode,
  ErrorStatusCode,
  createNamedErrorClass,
  toJson,
  BaseErrorSchema,
  safeParseExtendedGraphQlError,
  extendedGraphQlErrorHasErrorCode,
} from '@common/neo.tax';
import { addBreadcrumb, captureException, SeverityLevel } from '@sentry/browser';

import { LocalStorageKey } from '@neo.tax/shared/enums';
import { ValidOperations } from '@neo.tax/shared/graphql';
import { VersionGuardState } from '@neo.tax/shared/state';
import { clearFirebase, refreshFirebaseIdToken } from '@neo.tax/shared/third-parties';
import { ENTERPRISE_SSO_REQUIRED_REASON } from '@neo.tax/shared/utils';

const GraphQLRequestError = createNamedErrorClass('GraphQLRequestError');
const NetworkRequestError = createNamedErrorClass('NetworkRequestError');
const ApolloErrorLinkError = createNamedErrorClass('ApolloErrorLinkError');

enum ErrorType {
  NETWORK = 'network',
  GRAPHQL = 'graphql',
}

const ERROR_LEVEL: SeverityLevel = 'error';

const errorsToIgnore: { type: ErrorType; operationName: ValidOperations }[] = [
  { type: ErrorType.NETWORK, operationName: 'IsVersionOutdated' },
];

// Operations that should not trigger auto-logout on UNAUTHENTICATED errors
// This is useful for queries that might legitimately fail auth due to deleted resources
const skipAutoLogoutOnUnauthenticated: ValidOperations[] = ['GetRdProject'];

export const errorLink = onError(({ forward, graphQLErrors, networkError, operation }) => {
  const operationName = operation.operationName.trim();

  // There are certain errors we want to ignore to avoid spamming sentry / slack
  const ignoreError = errorsToIgnore.find((v) => v.operationName === operationName);

  if (operation.variables) {
    addBreadcrumb({
      type: 'query',
      category: ErrorType.GRAPHQL,
      level: ERROR_LEVEL,
      data: {
        variables: operation.variables,
      },
    });
  }

  // Retry-once layer for EMAIL_NOT_VERIFIED: if the backend auth guard rejects
  // a request because the cached ID token has email_verified=false, force a
  // token refresh and replay the operation. A second rejection means the user
  // genuinely hasn't verified, so we sign them out and send them to the resend
  // flow. Context flag prevents infinite retry loops.
  if (graphQLErrors && ignoreError?.type !== ErrorType.GRAPHQL) {
    const hasEmailNotVerifiedError = graphQLErrors.some((graphQlError) => {
      const parsed = safeParseExtendedGraphQlError(graphQlError, BaseErrorSchema);
      return parsed.success && extendedGraphQlErrorHasErrorCode(parsed.data, AuthErrorCode.EMAIL_NOT_VERIFIED);
    });
    if (hasEmailNotVerifiedError) {
      const context = operation.getContext();
      if (context.emailVerifiedRetried) {
        clearFirebase();
        window.location.assign('/verify-email-sent?reason=security-update');
        return;
      }
      operation.setContext({ emailVerifiedRetried: true });
      return fromPromise(refreshFirebaseIdToken()).flatMap(() => forward(operation));
    }
  }

  // The organization admits only its enterprise SSO provider and this session used something else. Nothing the
  // user can do in the app changes that and the session is useless, so it is cleared and they are sent to the
  // sign-in page with copy explaining why.
  if (graphQLErrors && ignoreError?.type !== ErrorType.GRAPHQL) {
    const hasEnterpriseSsoRequiredError = graphQLErrors.some((graphQlError) => {
      const parsed = safeParseExtendedGraphQlError(graphQlError, BaseErrorSchema);
      return parsed.success && extendedGraphQlErrorHasErrorCode(parsed.data, AuthErrorCode.ENTERPRISE_SSO_REQUIRED);
    });
    if (hasEnterpriseSsoRequiredError) {
      clearFirebase();
      window.location.assign(`/login?reason=${ENTERPRISE_SSO_REQUIRED_REASON}`);
      return;
    }
  }

  if (graphQLErrors && ignoreError?.type !== ErrorType.GRAPHQL) {
    graphQLErrors.forEach((graphQlError) => {
      // We don't know what kind of error this is and we don't really care so using the `BaseErrorSchema`
      // If the error cannot be successfully parsed using the standard schema then it has an unexpected structure so we throw an error
      const parsedGraphqlError = safeParseExtendedGraphQlError(graphQlError, BaseErrorSchema);
      if (!parsedGraphqlError.success) {
        return captureException(
          new ApolloErrorLinkError(`GraphQL error received is not ExtendedGraphQLErrors type: ${toJson(graphQlError)}`),
        );
      }

      if (extendedGraphQlErrorHasErrorCode(parsedGraphqlError.data, ErrorStatusCode.OUTDATED_VERSION)) {
        return VersionGuardState.updateIsVersionOutdated(true);
      }
      if (extendedGraphQlErrorHasErrorCode(parsedGraphqlError.data, ErrorStatusCode.UNAUTHENTICATED)) {
        // Skip auto-logout for specific operations that handle auth errors gracefully
        if (skipAutoLogoutOnUnauthenticated.includes(operationName as ValidOperations)) {
          return;
        }
        // Run logout logic and return the user to the login screen
        return clearFirebase();
      }
      if (extendedGraphQlErrorHasErrorCode(parsedGraphqlError.data, ErrorStatusCode.UNAUTHORIZED)) {
        // IP whitelist blocked access - store message and logout
        localStorage.setItem(
          LocalStorageKey.PERSIST_IP_WHITELIST_ERROR,
          'Access Denied: Please login to Neo.Tax from an IP address that is approved by your organization.',
        );
        return clearFirebase();
      }

      addBreadcrumb({
        type: 'query',
        category: ErrorType.GRAPHQL,
        level: ERROR_LEVEL,
        data: {
          ...graphQlError,
        },
      });

      // The server message is the same for every cause, so the title, tags and fingerprint are built from the error
      // codes. They hold only the codes and operation name, since the message and descriptions can carry personal data.
      const errorCodes = parsedGraphqlError.data.extensions.errors.map((error) => error.code);
      captureException(new GraphQLRequestError(`${operationName}: ${errorCodes.join(', ')}`), {
        tags: { 'graphql.operation': operationName, 'graphql.error_codes': errorCodes.join(',') },
        fingerprint: ['GraphQLRequestError', operationName, ...errorCodes],
      });
    });
  }

  if (networkError && ignoreError?.type !== ErrorType.NETWORK) {
    const message = networkError.message.trim();

    addBreadcrumb({
      type: 'http',
      category: ErrorType.NETWORK,
      level: ERROR_LEVEL,
      data: {
        message,
        stack: networkError.stack,
      },
    });

    captureException(new NetworkRequestError(`${operationName}: ${message}`));
  }

  // In case there is both types of errors we don't want to return in the conditionals
  // above and instead return after we've captured exceptions for each.
  if (graphQLErrors || networkError) {
    return;
  }

  // Catch-all in case we have an exception without messages
  // Note: This should never happen, but better to know when
  // an error occurs even if we don't have any details
  captureException(new ApolloErrorLinkError('Error link called with neither a graphQLError or a networkError'));
});
